import React, { useContext } from "react"
import { MdClose, MdSettings } from "react-icons/md"
import {
	MobergButton,
	MobergButtonShape,
	MobergButtonVariant,
	MobergCheckbox,
	MobergDropdown,
	MobergIconSize,
	MobergInputLabel,
	MobergNumberInput,
	MobergColumn,
	MobergRow,
	MobergShortTextInput,
	MobergTheme,
} from "../../../../../Moberg"
import { ModalityConfigurationDiv } from "../../../../../Constants/StyledComponents"
import { ColorSpectrum, HEATMAP_COLOR_SPECTRUM_OPTIONS } from "../../../../../Pages/Data/Visualize/DataReview/Types/ColorSpectrum"
import { HeatmapTraceConfig, HeatmapTraceConfigJSON, RenderStrategy, TraceConfigJSON, TraceOption } from "../../../../../Pages/Data/Visualize/DataReview/Types/Trace"
import { ConfigureWindowModalContext } from "../ConfigureWindowModal"
import { SpectrumSample } from "../SpectrumSample"
import { EditAnalysisModal } from "../../../../../Pages/Data/Visualize/DataReview/Components/Modals/EditAnalysis/EditAnalysisModal"
import { useModalProvider } from "../../../../../Providers/ModalProvider"
import { getAnalyticDisplayName, OnDemandAnalysis } from "../../../../../Pages/Data/Visualize/DataReview/Types/AnalysisDetails"
import { HeatmapGraphModalitiesContext } from "./HeatmapGraphModalities"

export const AnalyticHeatmapModalities: React.FC = () => {
	const { traceOptions, reasonAddMeasurementIsDisabled, updateGraphProperty } = useContext(ConfigureWindowModalContext)
	const { activeAnalyticTraces, handleDelete, handleColorSpectrumChange, handleValidationErrorChange, updateTrace } = useContext(HeatmapGraphModalitiesContext)
	const { createModal } = useModalProvider()
    const analyticTraceOptions = traceOptions.filter((traceOption: TraceOption) =>
        traceOption.value.onDemandAnalysis && traceOption.supportedRenderStrategies.includes(RenderStrategy.HEATMAP)
    )

	const addModality = () => {
		const newTrace: HeatmapTraceConfigJSON = {
			...analyticTraceOptions[0].value,
			id: `trace-${new Date(Date.now()).toISOString()}`, // ALWAYS override the existing ID with a new, unique one.
			renderStrategy: RenderStrategy.HEATMAP,
			lowerBound: 0,
			upperBound: 100,
			colorSpectrum: ColorSpectrum.INFERNO,
			isVisible: true
		}

		updateGraphProperty("traces", (previous: TraceConfigJSON[]) => ([...previous, newTrace]))
		updateTrace(newTrace.id, newTrace, { useModalitySpecificDefaults: true })
	}

	function traceAnalysisArgumentsChanged(trace: HeatmapTraceConfig, newArgs: OnDemandAnalysis | undefined) {
		updateTrace(trace.id, {
            ...trace,
			onDemandAnalysis: newArgs,
			dataKey: JSON.stringify(newArgs)
		})
	}

	return (
		<ModalityConfigurationDiv>
			<MobergColumn gap="16px">
				<div style={{ display: "grid", gridTemplateColumns: "224px 150px 175px 100px 112px 100px auto auto auto", columnGap: "16px", rowGap: "12px", alignItems: "center", maxWidth: "fit-content" }}>
					<MobergInputLabel text={"Analytic"} />
					<MobergInputLabel text={"Label"} />
					<MobergInputLabel text={"Color spectrum"} />
					<MobergInputLabel text={"Lower bound"} />
					<MobergInputLabel text={""} />
					<MobergInputLabel text={"Upper bound"} />
					<MobergInputLabel text={"Calculation"} />
					<MobergInputLabel text={"Visible"} />
					<MobergInputLabel text={""} />

					{activeAnalyticTraces.map(trace => {
						return (
							<>
								<MobergDropdown
									key={`${trace.id}-modalitySelector`}
									options={analyticTraceOptions}
									onChange={newTrace => updateTrace(trace.id, newTrace, { useModalitySpecificDefaults: true })}
									selectedValue={trace}
									equals={(a: HeatmapTraceConfigJSON, b: HeatmapTraceConfigJSON) =>
										a.onDemandAnalysis?.analytic === b.onDemandAnalysis?.analytic
										&& (a.isCompositePart
												? b.isCompositePart && a.compositeIndex === b.compositeIndex
												: true
											)
									}
									width={224}
								/>

								<MobergShortTextInput
									key={`${trace.id}-label`}
									limit={20}
									defaultValue={trace.name}
									value={trace.name}
									onChange={(name: string) => updateTrace(trace.id, { ...trace, name })}
									style={{ width: "150px" }}
								/>

								<MobergDropdown
									key={`${trace.id}-spectrumSelector`}
									selectedValue={trace.colorSpectrum}
									onChange={spectrum => handleColorSpectrumChange(spectrum, trace)}
									options={HEATMAP_COLOR_SPECTRUM_OPTIONS}
									width={175}
								/>

								<MobergNumberInput
									key={`${trace.id}-lowerBound`}
									defaultValue={trace.lowerBound}
									value={trace.lowerBound}
									onChange={lowerBound => updateTrace(trace.id, { ...trace, lowerBound: lowerBound ?? 0 })}
									onValidationErrorChange={error => handleValidationErrorChange(`${trace.id}-lowerBound`, error)}
								/>

								<MobergRow key={`${trace.id}-spectrumSample`}>
									<SpectrumSample colorSpectrum={trace.colorSpectrum} />
								</MobergRow>

								<MobergNumberInput
									key={`${trace.id}-upperBound`}
									defaultValue={trace.upperBound}
									value={trace.upperBound}
									onChange={upperBound => updateTrace(trace.id, { ...trace, upperBound: upperBound ?? 0 })}
									onValidationErrorChange={error => handleValidationErrorChange(`${trace.id}-upperBound`, error)}
								/>

								<MobergButton
									disabled={!trace.onDemandAnalysis}
									tooltip={trace.onDemandAnalysis
										? "Edit the calculation of " + getAnalyticDisplayName(trace.onDemandAnalysis.analytic)
										: "This modality is a raw signal."}
									shape={MobergButtonShape.SQUARE}
									onClick={() => {
										if (trace.onDemandAnalysis?.analytic) {
											createModal(<EditAnalysisModal
												analysis={trace.onDemandAnalysis}
												rawDataKey={trace.dataKey}
												onChange={(newArgs: OnDemandAnalysis | undefined) => traceAnalysisArgumentsChanged(trace, newArgs)}
											/>)
										}
									}}
								>
									<MdSettings size={MobergIconSize.REGULAR} />
								</MobergButton>

								<MobergCheckbox
									isChecked={trace.isVisible ?? true}
									onChange={isVisible => updateTrace(trace.id, { ...trace, isVisible })}
								/>

								<MobergButton
									key={`${trace.id}-deleteHeatmap`}
									shape={MobergButtonShape.SQUARE}
									onClick={() => handleDelete(trace.id)}
									style={{ padding: "6px" }}
									>
									<MdClose size={MobergIconSize.REGULAR} />
								</MobergButton>
							</>
						)
					})}

					<MobergButton onClick={addModality} theme={MobergTheme.BLUE} variant={MobergButtonVariant.OUTLINE} style={{ width: "224px" }}
						disabled={!!reasonAddMeasurementIsDisabled}
						tooltip={reasonAddMeasurementIsDisabled}>
						Add analytic
					</MobergButton>

				</div>
			</MobergColumn>
		</ModalityConfigurationDiv>
	)
}
